import { useState } from 'react';
import { Check, Copy, FileText, Smartphone, Wifi, X } from 'lucide-react';
import { toast } from 'sonner';
import { requisitarFaturasMes } from '@/hooks/useFaturasMes';
import { formatarDocumento } from '@/components/kanban/triagem/segmentoTriagem';

/* A TRAVA DAS 10H DAS FATURAS (Douglas 28/10/2026, maquete aprovada): a conta
   venceu e já está no CRM; ela copia o CNPJ, confere no SOLAR e marca Pagou ou
   Não pagou ali mesmo. Os botões são os da Triagem Faturas, apontados para a
   conta da trava (fn_qualidade_trava_conta_conferir). */

export type ContaConferir = {
  id: string; tipo: 'movel' | 'fixa'; numero: number; vencimento: string | null;
  pagamento: 'pago' | 'nao_pago' | null; feito: 'pagou' | 'nao_pagou' | null;
  documento: string | null; razao_social: string | null;
};
export type PedidoContaConferir = {
  pedido_id: string; numero_pedido: number | null; cliente: string | null; batido: boolean;
  conta?: ContaConferir | null; justificativa?: string | null;
};

const BTN = 'inline-flex h-6 shrink-0 items-center gap-1 rounded-md border border-border px-1.5 text-[11px] font-semibold text-foreground hover:border-primary/50 hover:text-primary disabled:opacity-40';
const VERDE = 'text-[#075E54] dark:text-[#25D366]';

const mesDa = (d: string | null | undefined) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR', { month: 'long' }) : '');
const diaMes = (d: string | null | undefined) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}` : '-');

function descricao(c: ContaConferir | null | undefined) {
  if (!c) return 'conta para conferir';
  return `${c.numero}ª conta · ${mesDa(c.vencimento)} · venceu ${diaMes(c.vencimento)}`;
}

function Linha({ p, aoMudar, aoJustificar }: { p: PedidoContaConferir; aoMudar: () => void; aoJustificar: () => void }) {
  const c = p.conta;
  const [ocupado, setOcupado] = useState(false);
  const copiar = () => {
    if (!c?.documento) return;
    void navigator.clipboard.writeText(c.documento.replace(/\D/g, '')).then(() => toast.success('CNPJ copiado para colar no SOLAR.'));
  };
  const marcar = async (situacao: 'pago' | 'nao_pago') => {
    if (!c) return;
    setOcupado(true);
    try {
      await requisitarFaturasMes(`${c.id}/pagamento`, { situacao });
      toast.success(`${c.numero}ª conta de ${mesDa(c.vencimento)} marcada como ${situacao === 'pago' ? 'paga' : 'não paga'}.`);
      aoMudar();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Não foi possível marcar a conta.'); }
    finally { setOcupado(false); }
  };

  const feito = p.batido && !p.justificativa;
  const sub = p.batido
    ? p.justificativa ? `${p.justificativa} · volta na próxima trava`
      : c?.feito === 'pagou' ? `${descricao(c)} · paga, conferida no SOLAR`
      : c?.feito === 'nao_pagou' ? `${descricao(c)} · não paga, conferida no SOLAR`
      : descricao(c)
    : null;

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 border-t border-border py-1.5">
      <div className="min-w-0">
        <p className="truncate text-[12.5px]" title={p.cliente || undefined}>
          <span className="font-semibold tabular-nums text-foreground">{p.numero_pedido ?? '-'}</span>
          <span className="text-muted-foreground"> · {p.cliente || '-'}</span>
        </p>
        {sub
          ? <p className={`text-[11px] leading-snug ${feito ? VERDE : 'text-muted-foreground'}`}>{sub}</p>
          : <p className="text-[11px] leading-snug text-muted-foreground">
              {c?.documento && <>
                <button type="button" onClick={copiar} title="Copiar o CNPJ para colar no SOLAR"
                  className="inline-flex items-center gap-1 tabular-nums hover:text-primary">
                  {formatarDocumento(c.documento)}<Copy className="h-3 w-3 text-primary" aria-hidden="true" />
                </button>
                <span aria-hidden="true"> · </span>
              </>}
              {descricao(c)}
            </p>}
      </div>
      <div className="flex items-center gap-1.5">
        {!p.batido && c && <>
          <button type="button" onClick={() => void marcar('pago')} disabled={ocupado} title="Conferi no SOLAR: o cliente pagou esta conta"
            className={BTN}><Check className="h-3 w-3" aria-hidden="true" />Pagou</button>
          <button type="button" onClick={() => void marcar('nao_pago')} disabled={ocupado} title="Conferi no SOLAR: o cliente não pagou esta conta"
            className={BTN}><X className="h-3 w-3" aria-hidden="true" />Não pagou</button>
        </>}
        {!p.batido && (
          <button type="button" onClick={aoJustificar} className={BTN}><FileText className="h-3 w-3" aria-hidden="true" />Justificar</button>
        )}
        <span className={`w-[64px] text-right text-[11px] ${feito ? `font-semibold ${VERDE}` : p.batido ? 'text-muted-foreground' : 'text-primary'}`}>
          {p.batido ? (p.justificativa ? 'justificado' : 'feito') : 'falta'}
        </span>
      </div>
    </div>
  );
}

export function TravaContaConferirLista({ pedidos, aoMudar, aoJustificar }: {
  pedidos: PedidoContaConferir[]; aoMudar: () => void; aoJustificar: (p: PedidoContaConferir) => void;
}) {
  const grupos = ([
    { tipo: 'movel', titulo: 'Móvel · celular', Icone: Smartphone },
    { tipo: 'fixa', titulo: 'Fixa · internet', Icone: Wifi },
  ] as const).map(g => ({ ...g, linhas: pedidos.filter(p => (p.conta?.tipo ?? 'movel') === g.tipo) })).filter(g => g.linhas.length > 0);
  return <>
    {grupos.map(g => (
      <div key={g.tipo} className="mb-2">
        <p className="mt-2 flex items-center gap-1.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <g.Icone className="h-3.5 w-3.5 text-primary" aria-hidden="true" />{g.titulo}
          <span className="ml-auto font-normal normal-case tracking-normal">faltam {g.linhas.filter(p => !p.batido).length}</span>
        </p>
        {g.linhas.map((p, i) => <Linha key={`${p.pedido_id}-${i}`} p={p} aoMudar={aoMudar} aoJustificar={() => aoJustificar(p)} />)}
      </div>
    ))}
  </>;
}
